import { Inject, Injectable, Optional } from '@angular/core';
import { ProcessingEngine, OperationDescriptor } from '../../main/models/processing-pipeline';
import { PROCESSING_ENGINES } from '../../main/models/processing-pipeline/processing-engines.contract';

/**
 * Central registry for processing engines.
 * Components query this to discover available operations.
 *
 * Engines arrive through the {@link PROCESSING_ENGINES} multi-provider rather
 * than being constructed here or in a module constructor, so which operations
 * exist is a DI question a build configuration can answer — see that token. The
 * imperative `register()` remains for tests and for anything that needs to add
 * an engine after construction.
 */
@Injectable({ providedIn: 'root' })
export class EngineRegistryService {
  private engines = new Map<string, ProcessingEngine>();

  constructor(
    /**
     * Optional so a TestBed that provides no engines still resolves, and so an
     * injector that has the service but not the pipeline module does not fail.
     */
    @Optional() @Inject(PROCESSING_ENGINES) contributed: readonly ProcessingEngine[] | null = null,
  ) {
    for (const engine of contributed ?? []) this.register(engine);
  }

  /** Register a processing engine. */
  register(engine: ProcessingEngine): void {
    this.engines.set(engine.id, engine);
  }

  /** Get all registered engines. */
  getEngines(): ProcessingEngine[] {
    return Array.from(this.engines.values());
  }

  /** Get engine by ID. */
  getEngine(id: string): ProcessingEngine | undefined {
    return this.engines.get(id);
  }

  /** Get all available operations across all engines. */
  getAllOperations(): OperationDescriptor[] {
    return this.getEngines().flatMap((e) => e.operations);
  }

  /** Get operations grouped by category. */
  getOperationsByCategory(): Map<string, OperationDescriptor[]> {
    const map = new Map<string, OperationDescriptor[]>();
    for (const op of this.getAllOperations()) {
      const list = map.get(op.category) || [];
      list.push(op);
      map.set(op.category, list);
    }
    return map;
  }

  /** Ensure the engine for a given operation is initialized. */
  async ensureEngineReady(engineId: string): Promise<ProcessingEngine> {
    const engine = this.engines.get(engineId);
    if (!engine) {
      throw new Error(`Engine "${engineId}" not registered`);
    }
    if (!engine.isInitialized()) {
      await engine.initialize();
    }
    return engine;
  }
}
